// The website's own forms — contact, volunteer, newsletter, quote — in one
// definition shared by every template. A template draws the fields;
// this owns where they go, what comes back and what the visitor is
// told, so Classic and Trailhead cannot disagree about whether a
// message was sent.
//
// It POSTs JSON with `fetch` and the server answers in JSON (a fetch
// that follows a redirect re-sends the POST — see CLAUDE.md). The URL
// depends on where the page is being served: /site/{org}/forms/{kind}
// on the app host, /forms/{kind} on the organization's own domain.
//
// In the editor canvas nothing is sent: a manager pressing Send on a
// preview would file a submission in their own inbox.

import { inject, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { siteLocation, sitePrefix } from './siteLocation.js';

export const FORM_KINDS = ['contact', 'volunteer', 'newsletter', 'quote'];

// Read through siteLocation so the server renders the same links the
// browser would — see that file.
export { sitePrefix };

export function onCanonicalHost() {
    return /^\/site\//.test(siteLocation().pathname);
}

export function useSiteForm(kind, { siteData, fields = {} } = {}) {
    const editing = inject('tiqEditorCanvas', false);
    const values = reactive({ ...fields, website: '' });
    const errors = ref({});
    const status = ref('idle'); // idle | sending | sent | error
    const message = ref('');

    // Turnstile only on the app host: the public key is bound to it,
    // and the server skips the check on an organization's own domain
    // for the same reason EventSignupController does.
    const turnstileKey = onCanonicalHost() ? (siteData?.site?.turnstile_site_key || null) : null;
    const turnstileToken = ref('');
    let widgetId = null;

    async function mountTurnstile(el) {
        if (!turnstileKey || !el || typeof window === 'undefined') return;
        try {
            const ts = await loadTurnstile();
            if (!ts) return;
            widgetId = ts.render(el, {
                sitekey: turnstileKey,
                callback: (t) => { turnstileToken.value = t; },
                'expired-callback': () => { turnstileToken.value = ''; },
                'error-callback': () => { turnstileToken.value = ''; },
                ...TURNSTILE_LOOK,
            });
        } catch {
            // A blocked script leaves the form usable; the server
            // decides whether a token was required.
        }
    }
    onBeforeUnmount(() => {
        if (widgetId !== null && window.turnstile) {
            try { window.turnstile.remove(widgetId); } catch { /* already gone */ }
        }
    });

    async function submit(extra = {}) {
        if (editing) {
            status.value = 'sent';
            message.value = 'Preview only. Visitors see the confirmation here.';
            return;
        }
        status.value = 'sending';
        errors.value = {};
        message.value = '';
        // The check runs unseen in the background; a visitor quick
        // enough to press before it finishes waits for it here rather
        // than meeting a "bot check failed" for something they never saw.
        if (turnstileKey && widgetId !== null) await waitForToken(turnstileToken);
        try {
            const res = await fetch(`${sitePrefix()}/forms/${kind}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ ...values, ...extra, 'cf-turnstile-response': turnstileToken.value || undefined }),
            });
            const body = await res.json().catch(() => ({}));
            // A token is good for one submission, so the next one needs
            // a fresh check whatever happened to this one.
            resetTurnstile();
            if (res.ok) {
                status.value = 'sent';
                message.value = body.message || 'Thanks, we got it.';
                for (const k of Object.keys(fields)) values[k] = Array.isArray(fields[k]) ? [] : '';
                return;
            }
            status.value = 'error';
            errors.value = Object.fromEntries(Object.entries(body.errors || {}).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]));
            message.value = body.message || (res.status === 429 ? 'Too many tries. Wait a minute and try again.' : 'That did not send. Check the form and try again.');
        } catch {
            status.value = 'error';
            message.value = 'No connection. Your message was not sent.';
        }
    }

    function resetTurnstile() {
        turnstileToken.value = '';
        if (widgetId !== null && window.turnstile) {
            try { window.turnstile.reset(widgetId); } catch { /* gone */ }
        }
    }

    return { values, errors, status, message, submit, turnstileKey, mountTurnstile };
}

/**
 * How the Cloudflare check looks on a public site. `interaction-only`
 * keeps the box off the page unless Cloudflare actually needs the
 * visitor to click something, which is rare; the always-visible box
 * sat under every form as a grey panel that belonged to no design.
 * English to match the site's own words, rather than the browser's
 * language, which put a Dutch "Gelukt!" under an English form.
 */
export const TURNSTILE_LOOK = Object.freeze({
    appearance: 'interaction-only',
    theme: 'light',
    size: 'flexible',
    language: 'en',
});

/** Resolves once the token is in, or after `ms` regardless; the server
 *  decides what a missing token means. */
export function waitForToken(tokenRef, ms = 8000) {
    if (tokenRef.value) return Promise.resolve();
    return new Promise((resolve) => {
        const stop = watch(tokenRef, (v) => { if (v) { stop(); clearTimeout(t); resolve(); } });
        const t = setTimeout(() => { stop(); resolve(); }, ms);
    });
}

let turnstilePromise = null;
function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstilePromise) return turnstilePromise;
    turnstilePromise = new Promise((resolve, reject) => {
        const existing = document.getElementById('tiq-cf-turnstile');
        if (existing) {
            existing.addEventListener('load', () => resolve(window.turnstile));
            existing.addEventListener('error', reject);
            return;
        }
        const s = document.createElement('script');
        s.id = 'tiq-cf-turnstile';
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = () => resolve(window.turnstile);
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return turnstilePromise;
}

// Where the Donate button sends a visitor. A plain GET the browser
// follows: the server checks the amount and answers with a redirect
// to PayPal, Stripe or the organization's own page, so no account
// detail is ever in the page.
export function donateUrl({ amount, frequency, method, purpose }) {
    const qs = new URLSearchParams();
    if (amount) qs.set('amount', String(amount));
    if (frequency) qs.set('frequency', frequency);
    if (method) qs.set('method', method);
    if (purpose) qs.set('purpose', purpose);
    const back = returnPath();
    if (back) qs.set('return', back);
    return `${sitePrefix()}/donate/checkout?${qs.toString()}`;
}

// The page to come back to from Stripe. A path only; the server
// refuses anything else.
function returnPath() {
    return siteLocation().pathname;
}

// What Stripe's return said, read once off the URL: `paid=donation`,
// `paid=shop`, `checkout=unavailable` from the cart, or `checkout=failed` when the checkout could not be
// opened. A block shows its own line for its own kind.
export function checkoutReturn() {
    const q = new URLSearchParams(siteLocation().search);
    return {
        paid: q.get('paid') || '',
        failed: q.get('checkout') === 'failed',
        // The cart named a product the shop can no longer sell (see
        // PublicWebsiteFormController::cartByOrg).
        unavailable: q.get('checkout') === 'unavailable' ? (q.get('item') || 'An item') : '',
    };
}
